/**
 * OCTOPUS - SectionPanel (item 6.17; redesign 2026-07-03)
 * Painel de uma seção de sinais ao vivo: cabeçalho com ícone em contêiner
 * tonalizado + contagem de sinais, grid denso de stat tiles (gauges radiais/
 * bússolas ocupam duas linhas). Apresentação pura — dados prontos do live-view.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import {
  Compass,
  Cog,
  Droplet,
  Zap,
  Thermometer,
  Navigation2,
  Activity,
  Info,
  Box,
  ChevronDown,
  ChevronRight,
  GripVertical
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import LiveSourceCard from './LiveSourceCard';
import EngineNotificationBadges from './EngineNotificationBadges';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Reordenar por arraste é desktop-only: o HTML5 DnD nativo não opera em toque e o
// kiosk do operador é touch — sem isto o handle seria um affordance morto lá.
const IS_COARSE_POINTER = typeof window !== 'undefined'
  && typeof window.matchMedia === 'function'
  && window.matchMedia('(pointer: coarse)').matches;

// Mapeamento de ícones
const ICON_MAP = {
  compass: Compass,
  engine: Cog,
  droplet: Droplet,
  zap: Zap,
  thermometer: Thermometer,
  navigation: Navigation2,
  activity: Activity,
  info: Info,
  box: Box,
};

const SectionPanel = ({ section, collapsible = false, equipmentId }) => {
  const [isExpanded, setIsExpanded] = useState(!collapsible);
  // Ordem MANUAL local (dono 2026-07-08): arrastar cards dentro do box. O backend
  // já devolve na ordem salva (display_order); mantemos uma ordem local por PATH p/
  // feedback imediato entre os refreshes do polling, re-sincronizando quando o
  // CONJUNTO de sinais muda (novo sinal entra no fim até ser reposicionado).
  const sources = section?.sources || [];
  const [orderPaths, setOrderPaths] = useState(null);
  const [dragPath, setDragPath] = useState(null);       // card sendo arrastado
  const [overPath, setOverPath] = useState(null);       // card-alvo (feedback de destino)
  const saveTimer = useRef(null);

  const pathsKey = sources.map((s) => s.path).sort().join('|');
  useEffect(() => {
    // (Re)inicializa a ordem local com a ordem do backend quando o conjunto muda.
    setOrderPaths(sources.map((s) => s.path));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathsKey]);

  // Cancela um PUT de ordem ainda pendente se o painel desmontar (troca de aba).
  useEffect(() => () => { if (saveTimer.current) clearTimeout(saveTimer.current); }, []);

  const persistOrder = useCallback((paths) => {
    if (!equipmentId) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    // debounce curto: salva ao assentar o arraste (persiste server-side).
    saveTimer.current = setTimeout(() => {
      axios.put(`${API}/equipments/${equipmentId}/live-order`, { paths },
        { headers: getAuthHeaders() }).catch((e) => {
        console.error('Erro ao salvar ordem dos cards:', e);
      });
    }, 400);
  }, [equipmentId]);

  const clearDrag = () => { setDragPath(null); setOverPath(null); };

  const handleDrop = (targetPath) => {
    if (!dragPath || dragPath === targetPath) { clearDrag(); return; }
    setOrderPaths((prev) => {
      const cur = prev || sources.map((s) => s.path);
      const next = cur.filter((p) => p !== dragPath);
      const at = next.indexOf(targetPath);
      next.splice(at < 0 ? next.length : at, 0, dragPath);
      persistOrder(next);
      return next;
    });
    clearDrag();
  };

  if (!section) return null;

  const { label, icon, regime } = section;
  const Icon = ICON_MAP[icon] || Box;

  // Renderiza na ordem local; sinais novos (ainda sem posição) vão ao fim.
  const byPath = new Map(sources.map((s) => [s.path, s]));
  const ordered = (orderPaths || sources.map((s) => s.path))
    .map((p) => byPath.get(p)).filter(Boolean);
  for (const s of sources) if (!orderPaths?.includes(s.path)) ordered.push(s);
  const canReorder = !!equipmentId && !IS_COARSE_POINTER && ordered.length > 1;

  return (
    <Card className="bg-card border-border">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2.5 text-sm font-semibold">
          <span className="flex h-7 w-7 items-center justify-center rounded-md bg-primary/10 text-primary shrink-0">
            <Icon className="h-4 w-4" />
          </span>
          <span className="flex-1 truncate">{label}</span>
          {regime?.label && (
            <span
              className="text-[11px] font-medium rounded-full px-2 py-0.5 bg-primary/15 text-primary shrink-0"
              title="Regime de operação atual do motor (RPM × consumo × engate — cadastro do equipamento)"
              data-testid="motor-regime-badge"
            >
              {regime.label}
            </span>
          )}
          <span className="text-xs font-normal text-muted-foreground tabular-nums">
            {sources.length} {sources.length === 1 ? 'sinal' : 'sinais'}
          </span>
          {collapsible && (
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              onClick={() => setIsExpanded(!isExpanded)}
            >
              {isExpanded ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
            </Button>
          )}
        </CardTitle>
      </CardHeader>

      {/* Avisos do painel do motor (D-02) — só acende para grupos de motor com
          engine_notifications ABERTOS (Plano 03); degradação silenciosa: campo
          ausente → zero DOM, card intacto (padrão não-crítico do LiveTab). */}
      {section.engine_notifications?.length > 0 && (
        <EngineNotificationBadges alerts={section.engine_notifications} />
      )}

      {(!collapsible || isExpanded) && (
        <CardContent className="pt-0">
          {ordered.length > 0 ? (
            // grid-flow-row (não dense): a ordem manual é respeitada fielmente — o
            // preenchimento denso reempacotaria cards e frustraria a posição escolhida.
            <div className="grid grid-cols-2 gap-2.5 auto-rows-min">
              {ordered.map((source, idx) => (
                <div
                  key={source.source_config_id || source.path || idx}
                  className={`group/card relative rounded-xl transition-shadow ${dragPath === source.path ? 'opacity-40' : ''} ${overPath === source.path && dragPath !== source.path ? 'ring-2 ring-primary/50' : ''}`}
                  // Drop target (o card inteiro recebe); a ORIGEM do arraste é só o
                  // handle (P2) — arrastar pelo corpo conflitaria com ler/tooltip.
                  onDragOver={canReorder ? (e) => e.preventDefault() : undefined}
                  onDragEnter={canReorder ? () => setOverPath(source.path) : undefined}
                  onDrop={canReorder ? () => handleDrop(source.path) : undefined}
                >
                  {canReorder && (
                    <span
                      className="absolute left-1 bottom-1 z-10 flex h-5 w-5 items-center justify-center rounded text-muted-foreground/50 opacity-40 group-hover/card:opacity-100 cursor-grab active:cursor-grabbing"
                      title="Arraste para reordenar (a posição fica salva automaticamente)"
                      aria-hidden="true"
                      draggable
                      onDragStart={() => setDragPath(source.path)}
                      onDragEnd={clearDrag}
                    >
                      <GripVertical className="h-3.5 w-3.5" />
                    </span>
                  )}
                  <LiveSourceCard
                    path={source.path}
                    displayName={source.display_name}
                    displayValue={source.display_value}
                    displayUnit={source.display_unit}
                    formatted={source.formatted}
                    stale={source.stale}
                    suspect={source.suspect}
                    sourceConflict={source.source_conflict}
                    alertZone={source.alert_zone}
                    decimalPlaces={source.decimal_places}
                    minValue={source.min_value}
                    maxValue={source.max_value}
                  />
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-4">
              Nenhum dado nesta seção
            </p>
          )}
        </CardContent>
      )}
    </Card>
  );
};

export default SectionPanel;
